import React, { type ReactElement, useEffect, useRef, useState } from 'react'
import { type IMapState, type IMapLibrary, type IMapProps, type IStyleableMapProps, type ILatLon } from '@/Root/Types/MapTypes'
import { type MapRootType } from '@/Root/MapRoot'
import MapContext from '@/Root/MapContext'
import { utils } from '@axdspub/axiom-ui-utilities'

interface IMapLoadState {
  loading: boolean
  loaded: boolean
  map?: MapRootType
  mapLibrary?: IMapLibrary
}

const Map = ({
  center = { lat: 62, lon: -145 },
  zoom = 5,
  baseLayerKey = 'hybrid',
  layers,
  tools,
  projection,
  setState,
  mapLibraryKey = 'openlayers',
  width = '100%',
  height = '600px',
  style = { background: '#CCC' },
  defaultClassName = 'map relative',
  className = '',
  debug = false,
  Tools,
  Legend,
  Overlay,
  onMapViewChange,
  onMapLoaded
}: IStyleableMapProps): ReactElement => {
  const mapRef = useRef(null)
  const initialMapLoadState = {
    loaded: false,
    loading: false,
    map: undefined,
    mapLibrary: undefined
  }

  const [mapLoadState, setMapLoadState] = useState<IMapLoadState>({ ...initialMapLoadState })
  const [error, setError] = useState<string | null>(null)
  const [mapState, setMapState] = useState<IMapState | undefined>(undefined)
  interface IHover extends ILatLon {
    color?: string
  }
  const [hoverLocation, setHoverLocation] = useState < IHover | undefined>(undefined)

  useEffect(() => {
    if (mapLibraryKey !== undefined && mapRef.current !== undefined && mapLoadState.mapLibrary === undefined && !mapLoadState.loaded && !mapLoadState.loading) {
      /* if (map !== undefined) {
        console.log('destroy!')
        map.destroy()
        setMap(undefined)
      } */
      mapLoadState.loading = true
      setMapLoadState({ ...mapLoadState })

      import(`./Libraries/${mapLibraryKey}`).then(module => {
        if (mapLoadState.loading) {
          const newMapLibrary = module.default
          mapLoadState.loaded = true
          mapLoadState.loading = false
          mapLoadState.mapLibrary = newMapLibrary
          setMapLoadState({ ...mapLoadState })
        }
      }).catch(e => {
        setError(`Error: unable to load ${mapLibraryKey}`)
      })
    }
  }, [mapRef, mapLoadState])

  useEffect(() => {
    if (mapLoadState.map !== undefined) {
      console.log('destroy!')
      mapLoadState.map.destroy()
    }
    setMapLoadState({ ...initialMapLoadState })
  }, [mapLibraryKey])

  useEffect(() => {
    if ((mapLoadState.map !== undefined) && baseLayerKey !== undefined && baseLayerKey !== mapLoadState.map.getBaseLayerKey()) {
      mapLoadState.map.setBaseLayerKey(baseLayerKey)
    }
  }, [baseLayerKey])

  useEffect(() => {
    if (mapLoadState.map === undefined && mapLoadState.mapLibrary !== undefined && mapLoadState.loaded) {
      const newMapProps: IMapProps = {
        center,
        zoom,
        baseLayerKey,
        layers,
        tools,
        projection,
        mapLibraryKey,
        mapLibrary: mapLoadState.mapLibrary,
        element: mapRef.current
      }
      const MapClass: any = mapLoadState.mapLibrary.Map as unknown as MapRootType
      const newMap: MapRootType = new MapClass(newMapProps)
      if (onMapLoaded !== undefined) {
        newMap.onMapLoaded(onMapLoaded)
      }
      newMap.onMapLoaded(() => {
        if (mapLoadState.loaded && mapLoadState.map === undefined) {
          mapLoadState.map = newMap
          setMapLoadState({ ...mapLoadState })
          if (onMapViewChange !== undefined) {
            newMap.onMapViewChange(onMapViewChange)
          }
          if (debug ?? true) {
            newMap.onMapViewChange((mapStateEvent) => {
              setMapState(mapStateEvent?.data?.mapState)
            })
            setMapState(newMap.getMapState())
            newMap.onPointerMove((e) => {
              const newLocation = e?.data?.latlon !== undefined
                ? {
                    ...e.data.latlon,
                    color: e.data.color
                  }
                : undefined
              setHoverLocation(newLocation)
            })
          }
          if (setState !== undefined) {
            setState(newMap)
          }
        }
      })
    }
  }, [mapLoadState])

  useEffect(() => {
    if (mapLoadState.map !== undefined) {
      if (mapLoadState.map?.center?.lat !== center.lat || mapLoadState.map?.center?.lon !== center.lon) {
        mapLoadState.map.setCenter(center)
      }
      if (mapLoadState.map?.zoom !== zoom) {
        mapLoadState.map.setZoom(zoom)
      }
    }
  }, [center, zoom])

  const styleOb = style !== undefined ? { ...style } : {}
  if (width !== undefined) {
    styleOb.width = style.width !== undefined ? style.width : width
  }
  if (height !== undefined) {
    styleOb.height = style.height !== undefined ? style.height : height
  }

  return (
    <MapContext.Provider value={{ map: mapLoadState.map }}>
      <>{
        error !== null
          ? <p>{error}</p>
          : <div className={utils.makeClassName({ defaultClassName, className })} style={styleOb}>
            {
              mapLoadState.map !== undefined && Tools !== undefined ? <div className="absolute right-5 top-5 z-40">{Tools}</div> : ''
            }
            {
              mapLoadState.map !== undefined && Legend !== undefined ? <div className="absolute right-0 top-0 bottom-0 z-20">{Legend}</div> : ''
            }
            {
              mapLoadState.map !== undefined && Overlay !== undefined ? <div className="absolute right-0 left-0 top-0 bottom-0 z-20 pointer-events-none">{Overlay}</div> : ''
            }
            {
              mapLoadState.map !== undefined && debug
                ? <div className='absolute gap-3 grid left-4 bottom-8 z-20 w-[300px] p-4 text-xs min-h-[100px] max-h-[500px] bg-white bg-opacity-60'>
                    <p className='font-bold text-lg'>Debug</p>
                    <p>Hover location: {hoverLocation !== undefined ? <strong>{hoverLocation.lat.toFixed(4)},{hoverLocation.lon.toFixed(4)}</strong> : ''}</p>
                    <p>Hover color: {hoverLocation?.color !== undefined ? <span className='w-[100px] h-[20px] ml-4 inline-block' style={{ backgroundColor: hoverLocation.color }}></span> : 'NA'}</p>
                    <div>
                    <p>Map state:</p>
                    <pre className='max-h-[200px] overflow-auto'>{JSON.stringify(mapState, null, 2)}</pre>
                    </div>
                  </div>
                : ''
            }
            <div className="map-wrap absolute top-0 bottom-0 left-0 right-0 z-10" data-testid="map-wrap" ref={mapRef}></div>
          </div>
      }</>
      </MapContext.Provider>
  )
}

export default Map
